import React, { useEffect, useState, useCallback } from 'react';
import useOrgRole from '../hooks/useOrgRole';
import { useAuth } from '../context/AuthContext';
import {
  getOrgMembers, addMember, updateMemberRoles, removeMember,
  saveAllowedDomains, generateToken, getTokens, revokeToken,
  listAllOrgs, setOrgPlan,
  provisionOrg,
  deactivateOrg, reactivateOrg, deleteOrg, restoreOrg,
} from '../firebase/orgs';
import { loadRunStats } from '../firebase/db';
import { createPortalSession } from '../firebase/billing';
import { listAuditLog } from '../firebase/audit';
import { resolveCreditsDisplay } from '../config/credits';
import { PLAN_LIMITS } from '../config/planLimits';
import ByocPanel from '../components/ByocPanel';
import CustomModelsPanel from '../components/CustomModelsPanel';
import CapabilitiesSection from './admin/CapabilitiesSection';
import ModelPolicySection from './admin/ModelPolicySection';

const ROLES       = ['admin', 'billing', 'it', 'seat'];
const ROLE_LABELS = { admin: 'Admin', billing: 'Billing', it: 'IT', seat: 'Seat' };
const ROLE_COLORS = { admin: '#f43f5e', billing: '#38bdf8', it: '#a78bfa', seat: '#4ade80' };

// ── Shared styles ─────────────────────────────────────────────────────────────

const card = {
  background: '#0f1923',
  border: '1px solid #1e3a5f',
  borderRadius: 10,
  padding: '28px 32px',
  marginBottom: 28,
};

const subCard = {
  background: '#0a1120',
  borderRadius: 8,
  padding: '18px 20px',
};

const sectionHeader = (color = '#e2e8f0') => ({
  color, fontWeight: 700, fontSize: '1rem',
  marginBottom: 20, letterSpacing: 0.5, textTransform: 'uppercase',
});

const subHeader = (color = '#94a3b8') => ({
  color, fontWeight: 600, fontSize: 13, marginBottom: 8,
});

const label = { color: '#94a3b8', fontSize: 13, marginBottom: 4 };
const value = { color: '#e2e8f0', fontSize: 15, fontWeight: 500 };

const inputStyle = {
  background: '#0f1923', border: '1px solid #1e3a5f', borderRadius: 6,
  color: '#e2e8f0', padding: '8px 12px', fontSize: 13, outline: 'none',
};

const btn = (color, outline = false) => ({
  background: outline ? 'transparent' : `${color}22`,
  border: `1px solid ${color}`,
  borderRadius: 6, color, fontWeight: 600, fontSize: 13,
  padding: '7px 16px', cursor: 'pointer',
});

// ── Role badge ────────────────────────────────────────────────────────────────

function RoleBadge({ role }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 9px', borderRadius: 12,
      fontSize: 11, fontWeight: 600, marginRight: 4,
      background: `${ROLE_COLORS[role]}22`,
      color: ROLE_COLORS[role],
      border: `1px solid ${ROLE_COLORS[role]}55`,
    }}>
      {ROLE_LABELS[role]}
    </span>
  );
}

// ── Admin section ─────────────────────────────────────────────────────────────

function AdminSection({ orgId, seatLimit, seatsUsed }) {
  const [members, setMembers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [addEmail, setAddEmail] = useState('');
  const [addRoles, setAddRoles] = useState([]);
  const [adding, setAdding]     = useState(false);
  const [error, setError]       = useState('');
  const [lastLink, setLastLink] = useState(null);

  // Derive the live seat count from the loaded member list; the seatsUsed prop is a
  // snapshot from sign-in and goes stale as members are added/removed in this panel.
  const liveSeatsUsed = loading ? seatsUsed : members.filter(m => (m.roles || []).includes('seat') && m.status === 'active').length;
  const atLimit = liveSeatsUsed >= seatLimit;

  const load = useCallback(async () => {
    setLoading(true);
    setMembers(await getOrgMembers(orgId));
    setLoading(false);
  }, [orgId]);

  useEffect(() => { if (orgId) load(); }, [orgId, load]);

  const toggleAddRole = r =>
    setAddRoles(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);

  const handleAdd = async () => {
    setError('');
    if (!addEmail.trim() || !addRoles.length) return;
    if (addRoles.includes('seat') && atLimit) {
      setError(`Seat limit reached (${seatLimit}/${seatLimit}). Add a seat block in Billing to continue.`);
      return;
    }
    setAdding(true);
    try {
      const res = await addMember(orgId, { email: addEmail.trim(), roles: addRoles });
      setLastLink(res?.setPasswordLink ? { email: addEmail.trim(), link: res.setPasswordLink } : null);
      setAddEmail(''); setAddRoles([]);
      await load();
    } catch (e) {
      const msg = String(e.message || '');
      if (e.status === 409 && msg.includes('seat limit')) {
        setError(`Seat limit reached (${seatLimit}/${seatLimit}). Add a seat block in Billing to continue.`);
      } else {
        setError(msg || 'Failed to add member. Please try again or email support@crimsonscan.com.');
      }
    } finally { setAdding(false); }
  };

  const handleRemove = async email => {
    setError('');
    try {
      await removeMember(orgId, email);
      await load();
    } catch (e) {
      setError(e.message || 'Failed to remove member');
    }
  };

  const handleRoleToggle = async (email, currentRoles, role) => {
    setError('');
    const next = currentRoles.includes(role)
      ? currentRoles.filter(r => r !== role)
      : [...currentRoles, role];
    if (next.includes('seat') && !currentRoles.includes('seat') && atLimit) {
      setError(`Seat limit reached (${seatLimit}/${seatLimit}).`);
      return;
    }
    try {
      await updateMemberRoles(orgId, email, next);
      await load();
    } catch (e) {
      setError(e.message || 'Failed to update roles');
    }
  };

  return (
    <div style={card}>
      <p style={sectionHeader('#f43f5e')}>Admin — Team Management · {orgId}</p>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 40, marginBottom: 24 }}>
        <div>
          <p style={label}>Seats Used</p>
          <p style={{ ...value, color: atLimit ? '#f87171' : '#4ade80' }}>{liveSeatsUsed} / {seatLimit}</p>
        </div>
        <div>
          <p style={label}>Total Members</p>
          <p style={value}>{members.length}</p>
        </div>
        <div>
          <p style={label}>Pending Invites</p>
          <p style={value}>{members.filter(m => m.status === 'invited').length}</p>
        </div>
      </div>

      {atLimit && (
        <div style={{ background: '#f8717118', border: '1px solid #f87171', borderRadius: 6, padding: '10px 14px', marginBottom: 20, color: '#f87171', fontSize: 13 }}>
          Seat limit reached ({seatLimit}/{seatLimit}). Add a 25-seat block in the Billing section to continue.
        </div>
      )}

      {/* Add member */}
      <div style={{ ...subCard, marginBottom: 20 }}>
        <p style={subHeader('#f43f5e')}>Add member by email</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <input
            type="email" placeholder="email@company.com"
            value={addEmail} onChange={e => setAddEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            style={{ ...inputStyle, flex: 1, minWidth: 200 }}
          />
          <button
            onClick={handleAdd}
            disabled={adding || !addEmail || !addRoles.length}
            style={{ ...btn('#f43f5e'), background: '#f43f5e', color: '#fff', border: 'none', opacity: (adding || !addEmail || !addRoles.length) ? 0.5 : 1 }}
          >
            {adding ? 'Adding…' : 'Add'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {ROLES.map(r => (
            <button key={r} onClick={() => toggleAddRole(r)} style={{
              padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: addRoles.includes(r) ? `${ROLE_COLORS[r]}22` : 'transparent',
              color: addRoles.includes(r) ? ROLE_COLORS[r] : '#475569',
              border: `1px solid ${addRoles.includes(r) ? ROLE_COLORS[r] : '#334155'}`,
            }}>
              {ROLE_LABELS[r]}
            </button>
          ))}
        </div>
        {error && <p style={{ color: '#f87171', fontSize: 12, marginTop: 8, marginBottom: 0 }}>{error}</p>}
        {lastLink && (
          <div style={{ marginTop: 10, background: '#4ade8018', border: '1px solid #4ade80', borderRadius: 6, padding: '10px 12px' }}>
            <p style={{ color: '#4ade80', fontSize: 12, margin: '0 0 6px' }}>
              Send this one-time set-password link to {lastLink.email}:
            </p>
            <input readOnly value={lastLink.link} onFocus={e => e.target.select()}
              style={{ ...inputStyle, width: '100%', fontSize: 11 }} />
          </div>
        )}
      </div>

      {/* Member list */}
      {loading ? (
        <p style={{ color: '#475569', fontSize: 13 }}>Loading…</p>
      ) : members.length === 0 ? (
        <p style={{ color: '#475569', fontSize: 13 }}>No members yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {members.map(m => (
            <div key={m.email} style={{ ...subCard, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 16px' }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <p style={{ color: '#e2e8f0', fontSize: 13, margin: 0 }}>{m.email}</p>
                <p style={{ color: m.status === 'active' ? '#4ade80' : '#64748b', fontSize: 11, margin: 0 }}>{m.status}</p>
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {ROLES.map(r => (
                  <button key={r}
                    onClick={() => handleRoleToggle(m.email, m.roles, r)}
                    title={m.roles.includes(r) ? `Remove ${ROLE_LABELS[r]}` : `Add ${ROLE_LABELS[r]}`}
                    style={{
                      padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      background: m.roles.includes(r) ? `${ROLE_COLORS[r]}22` : 'transparent',
                      color: m.roles.includes(r) ? ROLE_COLORS[r] : '#334155',
                      border: `1px solid ${m.roles.includes(r) ? ROLE_COLORS[r] : '#1e3a5f'}`,
                    }}
                  >
                    {ROLE_LABELS[r]}
                  </button>
                ))}
              </div>
              <button onClick={() => handleRemove(m.email)}
                style={{ background: 'none', border: '1px solid #3a1a1a', borderRadius: 6, color: '#f87171', fontSize: 12, padding: '4px 10px', cursor: 'pointer' }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── IT section ────────────────────────────────────────────────────────────────

function ITSection({ org, orgId }) {
  const { user } = useAuth() || {};

  // Tokens
  const [tokens, setTokens]         = useState([]);
  const [tokensLoading, setTL]      = useState(true);
  const [tokenName, setTokenName]   = useState('');
  const [generating, setGenerating] = useState(false);
  const [newToken, setNewToken]     = useState(null); // shown once after generation
  const [tokenError, setTokenError] = useState('');

  // Domain allowlist
  const [domains, setDomains]       = useState((org?.allowedDomains || []).join(', '));
  const [savingDomains, setSavingD] = useState(false);
  const [domainMsg, setDomainMsg]   = useState('');

  const loadTokens = useCallback(async () => {
    setTL(true);
    setTokens(await getTokens(orgId));
    setTL(false);
  }, [orgId]);

  useEffect(() => { if (orgId) loadTokens(); }, [orgId, loadTokens]);

  const handleGenerate = async () => {
    if (!tokenName.trim()) { setTokenError('Give the token a name first.'); return; }
    setTokenError('');
    setGenerating(true);
    try {
      const { tokenValue } = await generateToken(orgId, { name: tokenName.trim(), createdBy: user?.uid });
      setNewToken(tokenValue);
      setTokenName('');
      await loadTokens();
    } catch (e) {
      setTokenError(e.message || 'Failed to generate token');
    } finally { setGenerating(false); }
  };

  const handleRevoke = async tokenId => {
    await revokeToken(orgId, tokenId);
    await loadTokens();
  };

  const handleSaveDomains = async () => {
    setSavingD(true);
    setDomainMsg('');
    try {
      const list = domains.split(',').map(d => d.trim().toLowerCase()).filter(Boolean);
      await saveAllowedDomains(orgId, list);
      setDomainMsg('Saved.');
      setTimeout(() => setDomainMsg(''), 3000);
    } catch (e) {
      setDomainMsg('Failed to save.');
    } finally { setSavingD(false); }
  };

  return (
    <div style={card}>
      <p style={sectionHeader('#a78bfa')}>IT — Configuration</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* ── API Tokens ── */}
        <div style={subCard}>
          <p style={subHeader('#a78bfa')}>API Tokens</p>
          <p style={{ color: '#475569', fontSize: 13, marginBottom: 14 }}>
            Generate tokens for programmatic access to the CrimsonScan API. Tokens are shown once — copy them immediately.
          </p>

          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <input
              type="text" placeholder="Token name (e.g. CI Pipeline)"
              value={tokenName} onChange={e => setTokenName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleGenerate()}
              style={{ ...inputStyle, flex: 1, minWidth: 180 }}
            />
            <button onClick={handleGenerate} disabled={generating || !tokenName.trim()} style={{ ...btn('#a78bfa'), opacity: generating || !tokenName.trim() ? 0.5 : 1 }}>
              {generating ? 'Generating…' : 'Generate Token'}
            </button>
          </div>
          {tokenError && <p style={{ color: '#f87171', fontSize: 12, marginBottom: 10 }}>{tokenError}</p>}

          {/* Newly generated token — shown once */}
          {newToken && (
            <div style={{ background: '#0f192388', border: '1px solid #a78bfa', borderRadius: 6, padding: '12px 14px', marginBottom: 14 }}>
              <p style={{ color: '#a78bfa', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                Copy this token now — it won't be shown again.
              </p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <code style={{ color: '#e2e8f0', fontSize: 12, flex: 1, wordBreak: 'break-all' }}>{newToken}</code>
                <button
                  onClick={() => { navigator.clipboard.writeText(newToken); }}
                  style={{ ...btn('#a78bfa'), padding: '4px 10px', fontSize: 11, flexShrink: 0 }}
                >
                  Copy
                </button>
                <button
                  onClick={() => setNewToken(null)}
                  style={{ background: 'none', border: 'none', color: '#475569', fontSize: 18, cursor: 'pointer', padding: '0 4px' }}
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Token list */}
          {tokensLoading ? (
            <p style={{ color: '#475569', fontSize: 13 }}>Loading tokens…</p>
          ) : tokens.length === 0 ? (
            <p style={{ color: '#334155', fontSize: 13 }}>No tokens yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {tokens.map(t => (
                <div key={t.tokenId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #0f1923' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: '#e2e8f0', fontSize: 13, margin: 0 }}>{t.name}</p>
                    <p style={{ color: '#475569', fontSize: 11, margin: 0, fontFamily: 'monospace' }}>{t.preview}</p>
                  </div>
                  <p style={{ color: '#475569', fontSize: 11, margin: 0 }}>
                    {t.createdAt?.toDate?.()?.toLocaleDateString() || '—'}
                  </p>
                  <button onClick={() => handleRevoke(t.tokenId)}
                    style={{ background: 'none', border: '1px solid #3a1a1a', borderRadius: 6, color: '#f87171', fontSize: 11, padding: '3px 8px', cursor: 'pointer' }}
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Domain Allowlist ── */}
        <div style={subCard}>
          <p style={subHeader('#a78bfa')}>Domain Allowlist</p>
          <p style={{ color: '#475569', fontSize: 13, marginBottom: 12 }}>
            Restrict sign-in to specific email domains. Comma-separated (e.g. <code style={{ color: '#94a3b8' }}>company.com, lab.org</code>).
            Leave blank to allow any email.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="company.com, university.edu"
              value={domains}
              onChange={e => setDomains(e.target.value)}
              style={{ ...inputStyle, flex: 1, minWidth: 220 }}
            />
            <button onClick={handleSaveDomains} disabled={savingDomains} style={{ ...btn('#a78bfa'), opacity: savingDomains ? 0.5 : 1 }}>
              {savingDomains ? 'Saving…' : 'Save'}
            </button>
          </div>
          {domainMsg && <p style={{ color: '#4ade80', fontSize: 12, marginTop: 8, marginBottom: 0 }}>{domainMsg}</p>}
        </div>

        {/* ── BYOC ── */}
        <ByocPanel orgId={orgId} />

        {/* ── Custom Models ── */}
        <CustomModelsPanel orgId={orgId} />

      </div>
    </div>
  );
}

// ── Billing section ───────────────────────────────────────────────────────────

const PLAN_LABELS = { selfserve: 'Researcher / Team' };

export function BillingSection({ org }) {
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');

  if (!org) return null;

  const seatPct = Math.round(((org.seatsUsed || 0) / (org.seatLimit || 25)) * 100);

  // Compute credits — H15: missing fields mean "not tracked", never "depleted".
  const { tracked: creditsTracked, hasByoc, credits, creditLimit, creditPct, creditsLow, creditsDepleted } = resolveCreditsDisplay(org);

  const handleManageBilling = async () => {
    setPortalError('');
    setPortalLoading(true);
    try {
      const { url } = await createPortalSession(org.orgId);
      window.location.assign(url);
    } catch (err) {
      setPortalError(err.message || 'Unable to open the billing portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div style={card}>
      <p style={sectionHeader('#38bdf8')}>Billing</p>

      {/* Plan tier badge */}
      {org.plan === 'pilot' && (
        <div style={{
          background: '#f59e0b14', border: '1px solid #f59e0b55', borderRadius: 8,
          padding: '12px 16px', marginBottom: 20,
        }}>
          <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: 14 }}>Pilot Plan</span>
          <span style={{ color: '#94a3b8', fontSize: 12, marginLeft: 10 }}>
            {org.seatLimit || 5} seats · {5} runs/day · {64} candidates/run · Unlimited analyses
          </span>
        </div>
      )}

      {/* Self-serve billing management */}
      {org.plan === 'selfserve' && org.stripeCustomerId && (
        <div style={{ marginBottom: 20 }}>
          <button onClick={handleManageBilling} disabled={portalLoading} style={{ ...btn('#38bdf8'), opacity: portalLoading ? 0.6 : 1 }}>
            {portalLoading ? 'Opening…' : 'Manage billing'}
          </button>
          {portalError && (
            <div style={{ background: '#f8717118', border: '1px solid #f87171', borderRadius: 6, padding: '10px 14px', marginTop: 12, color: '#f87171', fontSize: 13 }}>
              {portalError}
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap', marginBottom: 24 }}>
        <div>
          <p style={label}>Plan</p>
          {/* M6: display the plan enforcement resolves — a missing field IS pilot
              (limits code defaults to pilot); showing 'paid' here contradicted the
              pilot banner the same org's users see. */}
          <p style={{ ...value, color: (org.plan || 'pilot') === 'pilot' ? '#f59e0b' : '#4ade80', textTransform: 'capitalize' }}>{PLAN_LABELS[org.plan] || org.plan || 'pilot'}</p>
        </div>
        <div>
          <p style={label}>Seat Usage</p>
          <p style={{ ...value, color: seatPct >= 100 ? '#f87171' : seatPct >= 80 ? '#fbbf24' : '#4ade80' }}>
            {org.seatsUsed || 0} / {org.seatLimit || 25}
          </p>
        </div>
        <div>
          <p style={label}>License Blocks</p>
          <p style={value}>{Math.ceil((org.seatLimit || 25) / 25)}</p>
        </div>
      </div>

      {/* Seat usage bar */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ height: 6, background: '#0a1120', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 3,
            width: `${Math.min(seatPct, 100)}%`,
            background: seatPct >= 100 ? '#f87171' : seatPct >= 80 ? '#fbbf24' : '#4ade80',
            transition: 'width 0.3s',
          }} />
        </div>
        <p style={{ color: '#334155', fontSize: 11, marginTop: 4, marginBottom: 0 }}>{seatPct}% of seats allocated</p>
      </div>

      {/* ── Compute Credits ── */}
      <div style={{ ...subCard, marginBottom: 24, border: creditsDepleted ? '1px solid #f87171' : creditsLow ? '1px solid #fbbf2444' : '1px solid transparent' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <p style={subHeader('#38bdf8')}>Compute Credits</p>
          {hasByoc && (
            <span style={{ fontSize: 11, fontWeight: 600, color: '#4ade80', background: '#4ade8018', border: '1px solid #4ade8044', borderRadius: 10, padding: '2px 10px' }}>
              BYOC Active
            </span>
          )}
        </div>

        {hasByoc ? (
          <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 0 }}>
            Your organisation is running on its own cloud infrastructure. Compute credits are not consumed — all generation and analysis
            runs execute against your BYOC resources. Manage your cloud configuration in the <strong style={{ color: '#a78bfa' }}>IT</strong> section.
          </p>
        ) : !creditsTracked ? (
          // H15: no credit fields on the org doc — credits aren't tracked for this
          // org (invoice billing). A meter here previously rendered "depleted" red.
          <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 0 }}>
            Compute usage is billed by invoice for this organisation — no credit balance is tracked.
            Contact <strong style={{ color: '#e2e8f0' }}>support@crimsonscan.com</strong> with any billing questions.
          </p>
        ) : (
          <>
            {creditsDepleted && (
              <div style={{ background: '#f8717118', border: '1px solid #f87171', borderRadius: 6, padding: '10px 14px', marginBottom: 14, color: '#f87171', fontSize: 13 }}>
                Compute credits depleted. Contact <strong>support@crimsonscan.com</strong> to purchase more or configure BYOC.
              </div>
            )}
            {creditsLow && !creditsDepleted && (
              <div style={{ background: '#fbbf2418', border: '1px solid #fbbf24', borderRadius: 6, padding: '10px 14px', marginBottom: 14, color: '#fbbf24', fontSize: 13 }}>
                Credits running low ({credits.toLocaleString()} remaining).
              </div>
            )}

            <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap', marginBottom: 14 }}>
              <div>
                <p style={label}>Balance</p>
                <p style={{ ...value, color: creditsDepleted ? '#f87171' : creditsLow ? '#fbbf24' : '#4ade80' }}>
                  {credits.toLocaleString()} credits
                </p>
              </div>
              <div>
                <p style={label}>Included per Cycle</p>
                <p style={value}>{creditLimit.toLocaleString()} credits</p>
              </div>
              <div>
                <p style={label}>Used This Cycle</p>
                <p style={value}>{(creditLimit - credits).toLocaleString()} credits</p>
              </div>
            </div>

            {/* Credit usage bar */}
            <div style={{ marginBottom: 0 }}>
              <div style={{ height: 6, background: '#060d18', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 3,
                  width: `${Math.min(creditPct, 100)}%`,
                  background: creditsDepleted ? '#f87171' : creditsLow ? '#fbbf24' : '#38bdf8',
                  transition: 'width 0.3s',
                }} />
              </div>
              <p style={{ color: '#334155', fontSize: 11, marginTop: 4, marginBottom: 0 }}>
                {creditPct}% remaining
              </p>
            </div>
          </>
        )}
      </div>

      {/* Contact */}
      <div style={{ ...subCard, border: '1px solid #1e3a5f' }}>
        <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 0 }}>
          Billing is managed by your account team. For invoices, seat changes, or credit purchases,
          contact <strong style={{ color: '#38bdf8' }}>support@crimsonscan.com</strong>.
        </p>
      </div>
    </div>
  );
}

// ── Usage Stats section ──────────────────────────────────────────────────────

function UsageStatsSection({ orgId, org }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('30d'); // '7d' | '30d' | '90d' | 'all'

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await loadRunStats(orgId);
        if (!cancelled) setRuns(data);
      } catch (e) {
        console.error('[stats] failed to load run stats:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  if (loading) {
    return (
      <div style={card}>
        <p style={sectionHeader('#38bdf8')}>Usage & Analytics</p>
        <p style={{ color: '#475569', fontSize: 13 }}>Loading usage data…</p>
      </div>
    );
  }

  // ── Time filtering ──────────────────────────────────────────────────────
  const now = Date.now();
  const rangeDays = range === '7d' ? 7 : range === '30d' ? 30 : range === '90d' ? 90 : Infinity;
  const cutoff = rangeDays === Infinity ? 0 : now - rangeDays * 86400000;
  const filtered = runs.filter(r => (new Date(r.ts).getTime() || 0) >= cutoff);

  // ── Aggregations ────────────────────────────────────────────────────────
  const totalRuns = filtered.length;
  const totalCandidates = filtered.reduce((s, r) => s + (r.candidateCount || 0), 0);
  const avgCandidatesPerRun = totalRuns > 0 ? Math.round(totalCandidates / totalRuns) : 0;

  // Unique active users (by uid)
  const userMap = {};
  for (const r of filtered) {
    const key = r.uid || r.userEmail || 'unknown';
    if (!userMap[key]) userMap[key] = { email: r.userEmail || key, runs: 0, candidates: 0 };
    userMap[key].runs++;
    userMap[key].candidates += r.candidateCount || 0;
  }
  const activeUsers = Object.keys(userMap).length;
  const userList = Object.values(userMap).sort((a, b) => b.runs - a.runs);

  // Category breakdown
  const catMap = {};
  for (const r of filtered) {
    const cat = r.selectedCategory || 'uncategorized';
    catMap[cat] = (catMap[cat] || 0) + 1;
  }
  const catList = Object.entries(catMap).sort((a, b) => b[1] - a[1]);

  // ── Runs per day (for chart) ────────────────────────────────────────────
  const dayMap = {};
  for (const r of filtered) {
    const d = new Date(r.ts).toISOString().slice(0, 10); // YYYY-MM-DD
    dayMap[d] = (dayMap[d] || 0) + 1;
  }
  // Fill gaps
  const chartDays = Math.min(rangeDays === Infinity ? 90 : rangeDays, 90);
  const dayEntries = [];
  for (let i = chartDays - 1; i >= 0; i--) {
    const d = new Date(now - i * 86400000).toISOString().slice(0, 10);
    dayEntries.push({ date: d, count: dayMap[d] || 0 });
  }
  const maxDayCount = Math.max(1, ...dayEntries.map(d => d.count));

  // Seat utilization
  const seatLimit = org?.seatLimit || 25;
  const seatsUsed = org?.seatsUsed || 0;
  const seatPct = Math.round((seatsUsed / seatLimit) * 100);

  // ── Chart dimensions ────────────────────────────────────────────────────
  const CW = 760, CH = 180, cPad = { t: 16, r: 12, b: 28, l: 40 };
  const barW = Math.max(2, Math.floor((CW - cPad.l - cPad.r) / dayEntries.length) - 1);
  const chartH = CH - cPad.t - cPad.b;

  const statCard = (lbl, val, accent = '#4ade80') => (
    <div style={{ background: '#0a1120', borderRadius: 8, padding: '16px 20px', minWidth: 120 }}>
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>{lbl}</div>
      <div style={{ color: accent, fontSize: 22, fontWeight: 700 }}>{val}</div>
    </div>
  );

  const rangeBtn = (key, lbl) => (
    <button
      key={key}
      onClick={() => setRange(key)}
      style={{
        background: range === key ? '#1e3a5f' : 'transparent',
        border: `1px solid ${range === key ? '#38bdf8' : '#1e3a5f'}`,
        borderRadius: 6, color: range === key ? '#38bdf8' : '#64748b',
        fontSize: 12, fontWeight: 600, padding: '4px 12px', cursor: 'pointer',
      }}
    >
      {lbl}
    </button>
  );

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <p style={{ ...sectionHeader('#38bdf8'), marginBottom: 0 }}>Usage & Analytics</p>
        <div style={{ display: 'flex', gap: 4 }}>
          {rangeBtn('7d', '7 days')}
          {rangeBtn('30d', '30 days')}
          {rangeBtn('90d', '90 days')}
          {rangeBtn('all', 'All time')}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 24 }}>
        {statCard('Total Runs', totalRuns.toLocaleString())}
        {statCard('Candidates', totalCandidates.toLocaleString(), '#38bdf8')}
        {statCard('Avg / Run', avgCandidatesPerRun, '#a78bfa')}
        {statCard('Active Users', activeUsers, '#f59e0b')}
        {statCard('Seats', `${seatsUsed}/${seatLimit}`, seatPct > 80 ? '#f87171' : '#4ade80')}
      </div>

      {/* Runs per day chart */}
      <div style={subCard}>
        <p style={subHeader()}>Runs per Day</p>
        <svg width={CW} height={CH} style={{ display: 'block' }}>
          {/* Y-axis gridlines */}
          {[0, 0.25, 0.5, 0.75, 1].map(f => {
            const y = cPad.t + (1 - f) * chartH;
            const v = Math.round(f * maxDayCount);
            return (
              <React.Fragment key={f}>
                <line x1={cPad.l} y1={y} x2={CW - cPad.r} y2={y} stroke="#1e3a5f" strokeWidth={0.5} />
                <text x={cPad.l - 6} y={y + 4} fill="#475569" fontSize={10} textAnchor="end">{v}</text>
              </React.Fragment>
            );
          })}
          {/* Bars */}
          {dayEntries.map((d, i) => {
            const x = cPad.l + i * (barW + 1);
            const h = (d.count / maxDayCount) * chartH;
            return (
              <rect key={d.date} x={x} y={cPad.t + chartH - h} width={barW} height={Math.max(h, 0)}
                fill={d.count > 0 ? '#38bdf8' : '#0f1923'} rx={1} opacity={0.85}>
                <title>{d.date}: {d.count} run{d.count !== 1 ? 's' : ''}</title>
              </rect>
            );
          })}
          {/* X-axis labels (first, middle, last) */}
          {[0, Math.floor(dayEntries.length / 2), dayEntries.length - 1].map(i => {
            if (!dayEntries[i]) return null;
            const x = cPad.l + i * (barW + 1) + barW / 2;
            return (
              <text key={i} x={x} y={CH - 4} fill="#475569" fontSize={10} textAnchor="middle">
                {dayEntries[i].date.slice(5)} {/* MM-DD */}
              </text>
            );
          })}
        </svg>
      </div>

      {/* Per-user breakdown */}
      {userList.length > 0 && (
        <div style={{ ...subCard, marginTop: 16 }}>
          <p style={subHeader()}>Usage by Team Member</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {userList.map((u, i) => {
              const pct = totalRuns > 0 ? (u.runs / totalRuns) * 100 : 0;
              return (
                <div key={u.email} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: i < userList.length - 1 ? '1px solid #0f1923' : 'none' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ color: '#e2e8f0', fontSize: 13, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</p>
                  </div>
                  <div style={{ width: 140 }}>
                    <div style={{ background: '#060d18', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                      <div style={{ background: '#38bdf8', height: '100%', width: `${pct}%`, borderRadius: 4 }} />
                    </div>
                  </div>
                  <div style={{ width: 70, textAlign: 'right' }}>
                    <span style={{ color: '#38bdf8', fontSize: 13, fontWeight: 600 }}>{u.runs}</span>
                    <span style={{ color: '#475569', fontSize: 11 }}> run{u.runs !== 1 ? 's' : ''}</span>
                  </div>
                  <div style={{ width: 90, textAlign: 'right' }}>
                    <span style={{ color: '#a78bfa', fontSize: 13, fontWeight: 600 }}>{u.candidates.toLocaleString()}</span>
                    <span style={{ color: '#475569', fontSize: 11 }}> cands</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Category breakdown */}
      {catList.length > 1 && (
        <div style={{ ...subCard, marginTop: 16 }}>
          <p style={subHeader()}>Runs by Category</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {catList.map(([cat, count]) => (
              <div key={cat} style={{ background: '#060d18', borderRadius: 6, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#e2e8f0', fontSize: 13 }}>{cat.replace(/_/g, ' ')}</span>
                <span style={{ background: '#1e3a5f', borderRadius: 10, color: '#38bdf8', fontSize: 11, fontWeight: 700, padding: '2px 8px' }}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {totalRuns === 0 && (
        <p style={{ color: '#334155', fontSize: 13, textAlign: 'center', padding: '24px 0', marginBottom: 0 }}>
          No runs in this time period. Usage data will appear here as your team runs discoveries.
        </p>
      )}
    </div>
  );
}

// ── Superadmin: provision a new org ───────────────────────────────────────────

function ProvisionOrgSection() {
  const [name, setName]         = useState('');
  const [plan, setPlan]         = useState('pilot');
  const [seatLimit, setSeatLimit] = useState(PLAN_LIMITS.pilot.maxSeats);
  const [dataPooling, setDataPooling] = useState(false);   // Discovery Cloud consent — unpooled unless the contract says pooled
  const [people, setPeople]     = useState([]);            // [{ email, roles }]
  const [pEmail, setPEmail]     = useState('');
  const [pRoles, setPRoles]     = useState([]);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');
  const [created, setCreated]   = useState(null);          // { orgId, members:[{email,roles,setPasswordLink}] }

  // Default the seat limit to the plan default until the org is created.
  useEffect(() => { setSeatLimit(PLAN_LIMITS[plan]?.maxSeats ?? PLAN_LIMITS.pilot.maxSeats); }, [plan]);

  const seats = people.filter(p => p.roles.includes('seat')).length;
  const hasAdmin = people.some(p => p.roles.includes('admin'));
  const canSubmit = name.trim() && people.length > 0 && hasAdmin && seats <= seatLimit && !busy;

  const togglePRole = r => setPRoles(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);

  const addPerson = () => {
    const email = pEmail.trim().toLowerCase();
    if (!email || !pRoles.length) return;
    if (people.some(p => p.email === email)) { setError(`${email} is already in the list`); return; }
    setPeople(prev => [...prev, { email, roles: pRoles }]);
    setPEmail(''); setPRoles([]); setError('');
  };
  const removePerson = email => setPeople(prev => prev.filter(p => p.email !== email));

  const submit = async () => {
    setError(''); setBusy(true);
    try {
      const res = await provisionOrg({ name: name.trim(), plan, seatLimit: Number(seatLimit), members: people, dataPooling });
      setCreated(res);
      setName(''); setPlan('pilot'); setPeople([]); setDataPooling(false);
    } catch (e) {
      setError(e.message || 'Failed to provision org');
    } finally { setBusy(false); }
  };

  return (
    <div style={card}>
      <p style={sectionHeader('#f43f5e')}>Superadmin — Provision Organisation</p>

      {/* Create form */}
      <div style={{ ...subCard, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <input placeholder="Organisation name" value={name} onChange={e => setName(e.target.value)}
            style={{ ...inputStyle, flex: 1, minWidth: 220 }} />
          <select value={plan} onChange={e => setPlan(e.target.value)} style={{ ...inputStyle, width: 120 }}>
            <option value="pilot">Pilot</option>
            <option value="paid">Paid</option>
          </select>
          <input type="number" min={seats || 1} value={seatLimit}
            onChange={e => setSeatLimit(e.target.value)} style={{ ...inputStyle, width: 110 }} />
        </div>

        {/* Discovery Cloud consent — recorded once at provisioning; ingestion filters on it */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <select value={dataPooling ? 'pooled' : 'unpooled'} onChange={e => setDataPooling(e.target.value === 'pooled')}
            style={{ ...inputStyle, width: 260 }}>
            <option value="unpooled">Unpooled — no data commingling (+33%)</option>
            <option value="pooled">Pooled — contributes to Discovery Cloud</option>
          </select>
          <span style={{ color: '#94a3b8', fontSize: 12 }}>
            Data tier per the signed contract — set once here; pooled orgs' generated candidates feed the Discovery Cloud.
          </span>
        </div>

        {/* People builder */}
        <p style={subHeader('#f43f5e')}>People ({seats} seats / {seatLimit})</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <input type="email" placeholder="person@company.com" value={pEmail}
            onChange={e => setPEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addPerson()}
            style={{ ...inputStyle, flex: 1, minWidth: 200 }} />
          <button onClick={addPerson} disabled={!pEmail.trim() || !pRoles.length}
            style={{ ...btn('#f43f5e'), opacity: (!pEmail.trim() || !pRoles.length) ? 0.5 : 1 }}>Add person</button>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {ROLES.map(r => (
            <button key={r} onClick={() => togglePRole(r)} style={{
              padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: pRoles.includes(r) ? `${ROLE_COLORS[r]}22` : 'transparent',
              color: pRoles.includes(r) ? ROLE_COLORS[r] : '#475569',
              border: `1px solid ${pRoles.includes(r) ? ROLE_COLORS[r] : '#334155'}`,
            }}>{ROLE_LABELS[r]}</button>
          ))}
        </div>

        {people.map(p => (
          <div key={p.email} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ color: '#e2e8f0', fontSize: 13, minWidth: 220 }}>{p.email}</span>
            <span style={{ display: 'flex', gap: 4 }}>{p.roles.map(r => <RoleBadge key={r} role={r} />)}</span>
            <button onClick={() => removePerson(p.email)} style={{ ...btn('#f87171', true), padding: '2px 10px' }}>Remove</button>
          </div>
        ))}

        {!hasAdmin && people.length > 0 && (
          <p style={{ color: '#f59e0b', fontSize: 12, marginTop: 8 }}>At least one person must have the Admin role.</p>
        )}
        {seats > seatLimit && (
          <p style={{ color: '#f87171', fontSize: 12, marginTop: 8 }}>Seats ({seats}) exceed the seat limit ({seatLimit}).</p>
        )}
        {error && <p style={{ color: '#f87171', fontSize: 12, marginTop: 8 }}>{error}</p>}

        <button onClick={submit} disabled={!canSubmit}
          style={{ ...btn('#f43f5e'), background: '#f43f5e', color: '#fff', border: 'none', marginTop: 12, opacity: canSubmit ? 1 : 0.5 }}>
          {busy ? 'Provisioning…' : 'Create organisation'}
        </button>
      </div>

      {/* Set-password links after creation */}
      {created && (
        <div style={{ ...subCard, marginBottom: 20, border: '1px solid #4ade80' }}>
          <p style={{ color: '#4ade80', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            {created.orgId} created. Send each person their one-time set-password link:
          </p>
          {created.members.map(m => (
            <div key={m.email} style={{ marginBottom: 8 }}>
              <p style={{ ...label, marginBottom: 2 }}>{m.email}</p>
              <input readOnly value={m.setPasswordLink} onFocus={e => e.target.select()}
                style={{ ...inputStyle, width: '100%', fontSize: 11 }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Superadmin cross-org stats ────────────────────────────────────────────────

function SuperadminStatsSection() {
  const [orgs, setOrgs] = useState([]);
  const [orgStats, setOrgStats] = useState({}); // { orgId: { runs, candidates, lastRunTs } }
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // orgId currently drilled into
  const [drillRuns, setDrillRuns] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const [actionBusy, setActionBusy] = useState(null);     // orgId currently acting on
  const [confirmDelete, setConfirmDelete] = useState(null); // orgId pending delete
  const [confirmText, setConfirmText] = useState('');
  const [restoredLinks, setRestoredLinks] = useState(null); // { orgId, members:[...] }
  const [actionError, setActionError] = useState('');

  const refreshOrgs = useCallback(async () => {
    try { setOrgs(await listAllOrgs()); } catch (e) { setActionError(e.message || 'refresh failed'); }
  }, []);

  const runAction = async (orgId, fn) => {
    setActionBusy(orgId); setActionError('');
    try { await fn(orgId); await refreshOrgs(); }
    catch (e) { setActionError(e.message || 'action failed'); }
    finally { setActionBusy(null); }
  };
  const onDeactivate = (orgId) => { if (window.confirm(`Deactivate ${orgId}? Users lose access until reactivated.`)) runAction(orgId, deactivateOrg); };
  const onReactivate = (orgId) => runAction(orgId, reactivateOrg);
  const doDelete = async (orgId) => {
    setActionBusy(orgId); setActionError('');
    try { await deleteOrg(orgId); setConfirmDelete(null); setConfirmText(''); await refreshOrgs(); }
    catch (e) { setActionError(e.message || 'delete failed'); }
    finally { setActionBusy(null); }
  };
  const onRestore = async (orgId) => {
    setActionBusy(orgId); setActionError('');
    try { const r = await restoreOrg(orgId); setRestoredLinks(r); await refreshOrgs(); }
    catch (e) { setActionError(e.message || 'restore failed'); }
    finally { setActionBusy(null); }
  };

  // Load all orgs, then load run stats for each
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const allOrgs = await listAllOrgs();
        if (cancelled) return;
        setOrgs(allOrgs);

        // Load run stats for every org in parallel
        const statEntries = await Promise.all(
          allOrgs.map(async (o) => {
            try {
              const runs = await loadRunStats(o.orgId);
              const totalRuns = runs.length;
              const totalCandidates = runs.reduce((s, r) => s + (r.candidateCount || 0), 0);
              const lastRunTs = runs.length > 0 ? new Date(runs[0].ts).getTime() : null;

              // Unique users
              const users = new Set(runs.map(r => r.uid || r.userEmail).filter(Boolean));

              // Runs in last 7 days
              const weekAgo = Date.now() - 7 * 86400000;
              const runsThisWeek = runs.filter(r => new Date(r.ts).getTime() >= weekAgo).length;

              // Runs in last 30 days
              const monthAgo = Date.now() - 30 * 86400000;
              const runsThisMonth = runs.filter(r => new Date(r.ts).getTime() >= monthAgo).length;

              return [o.orgId, { totalRuns, totalCandidates, lastRunTs, activeUsers: users.size, runsThisWeek, runsThisMonth }];
            } catch {
              return [o.orgId, { totalRuns: 0, totalCandidates: 0, lastRunTs: null, activeUsers: 0, runsThisWeek: 0, runsThisMonth: 0 }];
            }
          })
        );
        if (!cancelled) setOrgStats(Object.fromEntries(statEntries));
      } catch (e) {
        console.error('[superadmin-stats] failed:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Drill-down: load full run stats for selected org
  const handleDrill = useCallback(async (orgId) => {
    if (expanded === orgId) { setExpanded(null); return; }
    setExpanded(orgId);
    setDrillLoading(true);
    try {
      const runs = await loadRunStats(orgId);
      setDrillRuns(runs);
    } catch {
      setDrillRuns([]);
    } finally {
      setDrillLoading(false);
    }
  }, [expanded]);

  if (loading) {
    return (
      <div style={card}>
        <p style={sectionHeader('#f43f5e')}>Platform Overview</p>
        <p style={{ color: '#475569', fontSize: 13 }}>Loading cross-org data…</p>
      </div>
    );
  }

  // ── Platform-wide aggregations ──────────────────────────────────────────
  const archivedOrgs = orgs.filter(o => o.status === 'archived');
  const liveOrgs = orgs.filter(o => o.status !== 'archived');
  const totalOrgs = liveOrgs.length;
  const now = Date.now();
  const weekAgo = now - 7 * 86400000;
  const monthAgo = now - 30 * 86400000;

  // Active = at least 1 run in last 30 days; Cold = no runs in 30+ days but has runs; New = no runs ever
  const activeOrgs = liveOrgs.filter(o => (orgStats[o.orgId]?.lastRunTs || 0) >= monthAgo).length;
  const coldOrgs = liveOrgs.filter(o => {
    const s = orgStats[o.orgId];
    return s && s.totalRuns > 0 && (s.lastRunTs || 0) < monthAgo;
  }).length;
  const newOrgs = liveOrgs.filter(o => (orgStats[o.orgId]?.totalRuns || 0) === 0).length;

  const platformRuns = liveOrgs.reduce((s, o) => s + (orgStats[o.orgId]?.totalRuns || 0), 0);
  const platformCandidates = liveOrgs.reduce((s, o) => s + (orgStats[o.orgId]?.totalCandidates || 0), 0);
  const platformRunsWeek = liveOrgs.reduce((s, o) => s + (orgStats[o.orgId]?.runsThisWeek || 0), 0);
  const platformRunsMonth = liveOrgs.reduce((s, o) => s + (orgStats[o.orgId]?.runsThisMonth || 0), 0);
  const totalSeats = liveOrgs.reduce((s, o) => s + (o.seatsUsed || 0), 0);
  const totalSeatCapacity = liveOrgs.reduce((s, o) => s + (o.seatLimit || 25), 0);

  // Sort orgs: active first (by recent runs desc), then cold, then new
  const sortedOrgs = [...liveOrgs].sort((a, b) => {
    const sa = orgStats[a.orgId] || {};
    const sb = orgStats[b.orgId] || {};
    return (sb.lastRunTs || 0) - (sa.lastRunTs || 0);
  });

  // Churn risk: orgs with runs but none in 14+ days
  const churnRiskDays = 14;
  const churnCutoff = now - churnRiskDays * 86400000;
  const churnRiskOrgs = liveOrgs.filter(o => {
    const s = orgStats[o.orgId];
    return s && s.totalRuns > 0 && (s.lastRunTs || 0) < churnCutoff;
  });

  const statCard = (lbl, val, accent = '#4ade80') => (
    <div style={{ background: '#0a1120', borderRadius: 8, padding: '16px 20px', minWidth: 110 }}>
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>{lbl}</div>
      <div style={{ color: accent, fontSize: 22, fontWeight: 700 }}>{val}</div>
    </div>
  );

  const daysSince = (ts) => {
    if (!ts) return '—';
    const d = Math.floor((now - new Date(ts).getTime()) / 86400000);
    if (d === 0) return 'today';
    if (d === 1) return '1 day ago';
    return `${d}d ago`;
  };

  const statusBadge = (orgId) => {
    const s = orgStats[orgId];
    if (!s || s.totalRuns === 0) return <span style={{ color: '#475569', fontSize: 11 }}>No runs</span>;
    if ((s.lastRunTs || 0) >= weekAgo) return <span style={{ background: '#4ade8022', border: '1px solid #4ade80', borderRadius: 10, color: '#4ade80', fontSize: 10, fontWeight: 600, padding: '2px 8px' }}>Active</span>;
    if ((s.lastRunTs || 0) >= monthAgo) return <span style={{ background: '#f59e0b22', border: '1px solid #f59e0b', borderRadius: 10, color: '#f59e0b', fontSize: 10, fontWeight: 600, padding: '2px 8px' }}>Cooling</span>;
    return <span style={{ background: '#f8717122', border: '1px solid #f87171', borderRadius: 10, color: '#f87171', fontSize: 10, fontWeight: 600, padding: '2px 8px' }}>Cold</span>;
  };

  // Drill-down: per-user breakdown for selected org
  const drillUserMap = {};
  for (const r of drillRuns) {
    const key = r.userEmail || r.uid || 'unknown';
    if (!drillUserMap[key]) drillUserMap[key] = { email: key, runs: 0, candidates: 0, lastTs: 0 };
    drillUserMap[key].runs++;
    drillUserMap[key].candidates += r.candidateCount || 0;
    drillUserMap[key].lastTs = Math.max(drillUserMap[key].lastTs, new Date(r.ts).getTime() || 0);
  }
  const drillUsers = Object.values(drillUserMap).sort((a, b) => b.runs - a.runs);

  return (
    <div style={card}>
      <p style={sectionHeader('#f43f5e')}>Platform Overview</p>

      {/* Platform summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, marginBottom: 24 }}>
        {statCard('Orgs', totalOrgs)}
        {statCard('Active', activeOrgs, '#4ade80')}
        {statCard('New (0 runs)', newOrgs || '—', '#94a3b8')}
        {statCard('Inactive 30d+', coldOrgs || '—', '#f87171')}
        {statCard('Runs (all)', platformRuns.toLocaleString(), '#38bdf8')}
        {statCard('Runs (7d)', platformRunsWeek, '#38bdf8')}
        {statCard('Runs (30d)', platformRunsMonth, '#a78bfa')}
        {statCard('Candidates', platformCandidates.toLocaleString(), '#a78bfa')}
        {statCard('Seats', `${totalSeats}/${totalSeatCapacity}`, '#4ade80')}
      </div>

      {/* Churn risk alert */}
      {churnRiskOrgs.length > 0 && (
        <div style={{ background: '#f8717110', border: '1px solid #7f1d1d', borderRadius: 8, padding: '14px 18px', marginBottom: 20 }}>
          <div style={{ color: '#f87171', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
            Churn Risk — {churnRiskOrgs.length} org{churnRiskOrgs.length !== 1 ? 's' : ''} with no runs in {churnRiskDays}+ days
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {churnRiskOrgs.map(o => (
              <span key={o.orgId} style={{ background: '#0a1120', borderRadius: 6, padding: '4px 10px', color: '#e2e8f0', fontSize: 12 }}>
                {o.name || o.orgId}
                <span style={{ color: '#f87171', marginLeft: 6, fontSize: 11 }}>
                  {daysSince(orgStats[o.orgId]?.lastRunTs)}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Per-org table */}
      <div style={subCard}>
        <p style={subHeader()}>All Organisations</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {/* Header row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 1.6fr) 52px 66px 54px 74px 48px 56px 66px 150px', gap: 8, padding: '6px 0', borderBottom: '1px solid #1e3a5f' }}>
            <span style={{ color: '#64748b', fontSize: 11, fontWeight: 600 }}>Organisation</span>
            <span style={{ color: '#64748b', fontSize: 11, fontWeight: 600, textAlign: 'center' }}>Plan</span>
            <span style={{ color: '#64748b', fontSize: 11, fontWeight: 600, textAlign: 'right' }}>Status</span>
            <span style={{ color: '#64748b', fontSize: 11, fontWeight: 600, textAlign: 'right' }}>Runs</span>
            <span style={{ color: '#64748b', fontSize: 11, fontWeight: 600, textAlign: 'right' }}>Candidates</span>
            <span style={{ color: '#64748b', fontSize: 11, fontWeight: 600, textAlign: 'right' }}>Users</span>
            <span style={{ color: '#64748b', fontSize: 11, fontWeight: 600, textAlign: 'right' }}>Seats</span>
            <span style={{ color: '#64748b', fontSize: 11, fontWeight: 600, textAlign: 'right' }}>Last Run</span>
            <span style={{ color: '#64748b', fontSize: 11, fontWeight: 600, textAlign: 'right' }}>Actions</span>
          </div>

          {sortedOrgs.map((o, i) => {
            const s = orgStats[o.orgId] || {};
            const isExp = expanded === o.orgId;
            return (
              <React.Fragment key={o.orgId}>
                <div
                  onClick={() => handleDrill(o.orgId)}
                  style={{
                    display: 'grid', gridTemplateColumns: 'minmax(150px, 1.6fr) 52px 66px 54px 74px 48px 56px 66px 150px', gap: 8,
                    padding: '10px 0', borderBottom: '1px solid #0f1923', cursor: 'pointer',
                    background: isExp ? '#0f192388' : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <span title={o.name || o.orgId} style={{ color: isExp ? '#38bdf8' : '#e2e8f0', fontSize: 13, fontWeight: isExp ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {isExp ? '▾' : '▸'} {o.name || o.orgId}
                    </span>
                    {o.status === 'suspended' && (
                      <span style={{ color: '#f87171', fontSize: 10, fontWeight: 600 }}>SUSPENDED</span>
                    )}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        // M6: toggle from the EFFECTIVE plan (missing field = pilot,
                        // matching enforcement). The old `o.plan === 'pilot'` check made
                        // a missing-plan org display PAID yet toggle to... pilot.
                        const next = (o.plan || 'pilot') === 'pilot' ? 'paid' : 'pilot';
                        runAction(o.orgId, (orgId) => setOrgPlan(orgId, next));
                      }}
                      title="Click to toggle plan"
                      style={{
                        background: (o.plan || 'pilot') === 'pilot' ? '#f59e0b22' : '#4ade8022',
                        border: `1px solid ${(o.plan || 'pilot') === 'pilot' ? '#f59e0b' : '#4ade80'}`,
                        borderRadius: 10, color: (o.plan || 'pilot') === 'pilot' ? '#f59e0b' : '#4ade80',
                        fontSize: 10, fontWeight: 600, padding: '2px 8px', cursor: 'pointer',
                      }}
                    >
                      {(o.plan || 'pilot').toUpperCase()}
                    </span>
                  </div>
                  <div style={{ textAlign: 'right' }}>{statusBadge(o.orgId)}</div>
                  <div style={{ color: '#38bdf8', fontSize: 13, fontWeight: 600, textAlign: 'right' }}>{s.totalRuns || 0}</div>
                  <div style={{ color: '#a78bfa', fontSize: 13, fontWeight: 600, textAlign: 'right' }}>{(s.totalCandidates || 0).toLocaleString()}</div>
                  <div style={{ color: '#e2e8f0', fontSize: 13, textAlign: 'right' }}>{s.activeUsers || 0}</div>
                  <div style={{ color: '#e2e8f0', fontSize: 13, textAlign: 'right' }}>{o.seatsUsed || 0}/{o.seatLimit || 25}</div>
                  <div style={{ color: '#64748b', fontSize: 12, textAlign: 'right' }}>{daysSince(s.lastRunTs)}</div>
                  <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                    {o.status !== 'suspended' && (
                      <button disabled={actionBusy === o.orgId} onClick={() => onDeactivate(o.orgId)}
                        style={{ ...btn('#f59e0b', true), padding: '2px 10px' }}>Deactivate</button>
                    )}
                    {o.status === 'suspended' && (
                      <button disabled={actionBusy === o.orgId} onClick={() => onReactivate(o.orgId)}
                        style={{ ...btn('#4ade80', true), padding: '2px 10px' }}>Reactivate</button>
                    )}
                    <button disabled={actionBusy === o.orgId} onClick={() => { setConfirmDelete(o.orgId); setConfirmText(''); }}
                      style={{ ...btn('#f87171', true), padding: '2px 10px' }}>Delete</button>
                  </span>
                </div>

                {/* Drill-down panel */}
                {isExp && (
                  <div style={{ background: '#060d18', borderRadius: 8, padding: '16px 20px', margin: '6px 0 10px 20px' }}>
                    {drillLoading ? (
                      <p style={{ color: '#475569', fontSize: 13, margin: 0 }}>Loading…</p>
                    ) : drillUsers.length === 0 ? (
                      <p style={{ color: '#334155', fontSize: 13, margin: 0 }}>No runs recorded for this org.</p>
                    ) : (
                      <>
                        <p style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Team Member Breakdown</p>
                        {drillUsers.map((u, ui) => (
                          <div key={u.email} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderBottom: ui < drillUsers.length - 1 ? '1px solid #0a1120' : 'none' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ color: '#e2e8f0', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{u.email}</span>
                            </div>
                            <span style={{ color: '#38bdf8', fontSize: 12, fontWeight: 600, width: 65, textAlign: 'right' }}>{u.runs} run{u.runs !== 1 ? 's' : ''}</span>
                            <span style={{ color: '#a78bfa', fontSize: 12, width: 80, textAlign: 'right' }}>{u.candidates.toLocaleString()} cands</span>
                            <span style={{ color: '#64748b', fontSize: 11, width: 65, textAlign: 'right' }}>{daysSince(u.lastTs)}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {totalOrgs === 0 && (
        <p style={{ color: '#334155', fontSize: 13, textAlign: 'center', padding: '24px 0', marginBottom: 0 }}>
          No organisations found.
        </p>
      )}

      {actionError && (
        <p style={{ color: '#f87171', fontSize: 12, marginTop: 10 }}>{actionError}</p>
      )}

      {confirmDelete && (
        <div style={{ ...subCard, marginTop: 16, border: '1px solid #f87171' }}>
          <p style={{ color: '#f87171', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Delete {confirmDelete}? This removes all members + their sign-in accounts. The org and its data are archived (restorable ~6 months). Type <b>{confirmDelete}</b> to confirm.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder={confirmDelete}
              style={{ ...inputStyle, flex: 1 }} />
            <button disabled={confirmText !== confirmDelete || actionBusy === confirmDelete}
              onClick={() => doDelete(confirmDelete)}
              style={{ ...btn('#f87171'), background: '#f87171', color: '#fff', border: 'none', opacity: (confirmText !== confirmDelete) ? 0.5 : 1 }}>
              {actionBusy === confirmDelete ? 'Deleting…' : 'Delete org'}
            </button>
            <button onClick={() => { setConfirmDelete(null); setConfirmText(''); }} style={{ ...btn('#94a3b8', true) }}>Cancel</button>
          </div>
        </div>
      )}

      {restoredLinks && (
        <div style={{ ...subCard, marginTop: 16, border: '1px solid #4ade80' }}>
          <p style={{ color: '#4ade80', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            {restoredLinks.orgId} restored. Send each member their new set-password link:
          </p>
          {restoredLinks.members.map(m => (
            <div key={m.email} style={{ marginBottom: 8 }}>
              <p style={{ ...label, marginBottom: 2 }}>{m.email}</p>
              <input readOnly value={m.setPasswordLink} onFocus={e => e.target.select()} style={{ ...inputStyle, width: '100%', fontSize: 11 }} />
            </div>
          ))}
          <button onClick={() => setRestoredLinks(null)} style={{ ...btn('#94a3b8', true), marginTop: 4 }}>Dismiss</button>
        </div>
      )}

      {archivedOrgs.length > 0 && (
        <div style={{ ...subCard, marginTop: 16 }}>
          <p style={subHeader('#94a3b8')}>Archived organisations</p>
          {archivedOrgs.map(o => (
            <div key={o.orgId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #16233a' }}>
              <span style={{ color: '#e2e8f0', fontSize: 13, flex: 1 }}>{o.name || o.orgId}</span>
              <span style={{ ...label, marginBottom: 0 }}>
                retain until {o.retainUntil?.seconds ? new Date(o.retainUntil.seconds * 1000).toISOString().slice(0, 10) : '—'}
              </span>
              <button disabled={actionBusy === o.orgId} onClick={() => onRestore(o.orgId)}
                style={{ ...btn('#4ade80', true), padding: '3px 12px' }}>{actionBusy === o.orgId ? 'Restoring…' : 'Restore'}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AuditLogSection() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const rows = await listAuditLog(50); if (!cancelled) setEntries(rows); }
      catch (e) { if (!cancelled) setError(e.message || 'failed to load audit log'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const fmtTs = (ts) => ts?.seconds ? new Date(ts.seconds * 1000).toISOString().slice(0, 19).replace('T', ' ') : '—';

  return (
    <div style={{ background: '#0f1923', borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
      <p style={{ color: '#94a3b8', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Audit Log — recent privileged actions</p>
      {loading ? (
        <p style={{ color: '#475569', fontSize: 13, margin: 0 }}>Loading…</p>
      ) : error ? (
        <p style={{ color: '#f87171', fontSize: 12, margin: 0 }}>{error}</p>
      ) : entries.length === 0 ? (
        <p style={{ color: '#334155', fontSize: 13, margin: 0 }}>No audit entries yet.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '160px 130px 1fr 1fr', gap: 8 }}>
          <span style={{ color: '#64748b', fontSize: 11, fontWeight: 600 }}>Time (UTC)</span>
          <span style={{ color: '#64748b', fontSize: 11, fontWeight: 600 }}>Action</span>
          <span style={{ color: '#64748b', fontSize: 11, fontWeight: 600 }}>Actor</span>
          <span style={{ color: '#64748b', fontSize: 11, fontWeight: 600 }}>Target</span>
          {entries.map((e) => (
            <React.Fragment key={e.id}>
              <span style={{ color: '#64748b', fontSize: 12 }}>{fmtTs(e.ts)}</span>
              <span style={{ color: '#38bdf8', fontSize: 12, fontWeight: 600 }}>{e.action}</span>
              <span style={{ color: '#e2e8f0', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.actorEmail || e.actorUid || '—'}</span>
              <span style={{ color: '#e2e8f0', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {e.targetOrgId || '—'}{e.targetEmail ? ` · ${e.targetEmail}` : ''}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main portal ───────────────────────────────────────────────────────────────

export default function AdminPortal() {
  const { isAdmin, isBilling, isIT, isSuperAdmin, org: authOrg, orgMember } = useOrgRole();
  const [superOrg, setSuperOrg] = useState(null);

  // Superadmins have no orgMember, so load the demo org data directly
  const orgId = orgMember?.orgId || 'crimsonscan';
  useEffect(() => {
    if (isSuperAdmin && !authOrg) {
      import('../firebase/orgs').then(({ getOrg }) =>
        getOrg(orgId).then(o => { if (o) setSuperOrg(o); })
      );
    }
  }, [isSuperAdmin, authOrg, orgId]);

  const org = authOrg || superOrg;

  if (!orgMember && !isSuperAdmin) {
    return (
      <div style={{ minHeight: 'calc(100vh - 112px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#475569' }}>No organisation found for your account.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1120, margin: '0 auto', padding: '40px 24px' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <h1 style={{ color: '#e2e8f0', fontWeight: 700, fontSize: '1.6rem', margin: 0 }}>
            {org?.name || (isSuperAdmin ? 'CrimsonScan' : 'Your Organisation')}
          </h1>
          {isSuperAdmin && (
            <span style={{ background: '#f43f5e22', border: '1px solid #f43f5e', borderRadius: 10, color: '#f43f5e', fontSize: 10, fontWeight: 700, padding: '2px 10px', letterSpacing: 0.5 }}>
              SUPERADMIN
            </span>
          )}
          {org?.plan === 'pilot' && (
            <span style={{ background: '#f59e0b22', border: '1px solid #f59e0b55', borderRadius: 10, color: '#f59e0b', fontSize: 10, fontWeight: 700, padding: '2px 8px', letterSpacing: 0.5 }}>
              PILOT
            </span>
          )}
        </div>
        {orgMember && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
            {orgMember.roles.map(r => <RoleBadge key={r} role={r} />)}
          </div>
        )}
        {org?.status === 'suspended' && (
          <div style={{ marginTop: 12, background: '#f8717118', border: '1px solid #f87171', borderRadius: 6, padding: '10px 14px', color: '#f87171', fontSize: 13 }}>
            This organisation is suspended. Contact support or update your payment method.
          </div>
        )}
      </div>

      {!isAdmin && !isBilling && !isIT && !isSuperAdmin && (
        <p style={{ color: '#475569' }}>Your account has no management roles assigned. Contact your org admin.</p>
      )}

      {isSuperAdmin && <SuperadminStatsSection />}
      {isSuperAdmin && <AuditLogSection />}
      {isSuperAdmin && <ProvisionOrgSection />}
      {(isAdmin   || isSuperAdmin) && <AdminSection orgId={orgId} seatLimit={org?.seatLimit || 25} seatsUsed={org?.seatsUsed || 0} />}
      {(isAdmin   || isSuperAdmin) && <UsageStatsSection orgId={orgId} org={org} />}
      {(isAdmin   || isSuperAdmin) && <CapabilitiesSection orgId={orgId} />}
      {(isAdmin   || isSuperAdmin) && <ModelPolicySection orgId={orgId} />}
      {(isIT      || isSuperAdmin) && <ITSection org={org} orgId={orgId} />}
      {(isBilling || isSuperAdmin) && <BillingSection org={org} />}
    </div>
  );
}
